import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AppShell from '../components/AppShell';
import SyncIconButton from '../components/SyncIconButton';
import { deleteDraft } from '../offline/drafts';
import { useDeviceDrafts, useNetworkStatus } from '../offline/useOffline';
import {
  lastPrefetchAt,
  prefetchReferenceData,
  refreshCodeCounter,
  subscribeSync,
  syncAll,
  syncBlocker,
} from '../offline/sync';
import { getCachedUser } from '../offline/session';
import { isBrowserOffline } from '../offline/network';

const dateFormatter = new Intl.DateTimeFormat('ar-SA-u-nu-latn-ca-gregory', { dateStyle: 'medium', timeStyle: 'short' });

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : dateFormatter.format(date);
}

export default function OfflineSync() {
  const offline = useNetworkStatus();
  // الأزرار تُعطَّل فقط إن كانت الشبكة مقطوعة تمامًا؛ إن كان الخادم لا يرد، تتحقق المزامنة منه فورًا.
  const browserOffline = isBrowserOffline();
  const drafts = useDeviceDrafts();
  const [progress, setProgress] = useState({ running: false, done: 0, total: 0 });
  const [results, setResults] = useState([]);
  const [prefetchedAt, setPrefetchedAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const user = getCachedUser();

  useEffect(() => subscribeSync(setProgress), []);
  useEffect(() => {
    lastPrefetchAt().then(setPrefetchedAt);
  }, []);

  const completedCount = drafts.filter((draft) => draft.status === 'completed').length;

  const runSync = async (onlyCompleted) => {
    const next = await syncAll({ onlyCompleted });
    setResults(next);
  };

  const removeDraft = async (draft) => {
    const message = draft.server_id
      ? `الفحص ${draft.code} أُنشئ في الخادم، لكن بعض بياناته أو صوره لم تُرفع بعد. حذفه من الجهاز يلغي ما لم يُرفع نهائيًا. متابعة؟`
      : `حذف الفحص ${draft.code} من الجهاز نهائيًا؟ لم يُرفع إلى الخادم، ولا يمكن استرجاعه.`;
    if (!window.confirm(message)) return;
    await deleteDraft(draft.id);
  };

  const refreshReference = async () => {
    setRefreshing(true);
    try {
      await Promise.all([prefetchReferenceData({ force: true }), refreshCodeCounter()]);
      setPrefetchedAt(await lastPrefetchAt());
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <AppShell title="فحوصات الجهاز والمزامنة">
      <div className="space-y-4">
        <div className={`rounded-2xl border p-4 ${offline ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
          <p className="font-bold">{offline ? '⚠ لا يوجد اتصال بالخادم' : '✓ الاتصال متاح'}</p>
          <p className="mt-1 text-sm text-slate-600">
            آخر تنزيل للبيانات المرجعية (الشركات، الموديلات، بنود الفحص): {formatDate(prefetchedAt)}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => runSync(true)}
              disabled={browserOffline || progress.running || !completedCount}
              className="rounded-xl bg-primary px-4 py-2 text-white font-bold disabled:opacity-50"
            >
              {progress.running ? `جاري الرفع ${progress.done}/${progress.total}...` : `رفع الفحوصات المكتملة (${completedCount})`}
            </button>
            <button
              type="button"
              onClick={() => runSync(false)}
              disabled={browserOffline || progress.running || !drafts.length}
              className="rounded-xl border border-primary px-4 py-2 text-primary disabled:opacity-50"
            >
              رفع الكل حتى غير المكتملة
            </button>
            <button
              type="button"
              onClick={refreshReference}
              disabled={browserOffline || refreshing}
              className="rounded-xl border px-4 py-2 disabled:opacity-50"
            >
              {refreshing ? 'جاري التحديث...' : 'تحديث البيانات المرجعية'}
            </button>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            الفحوصات المكتملة تُرفع تلقائيًا عند عودة الاتصال. الفحص غير المكتمل يبقى على الجهاز حتى تنهيه أو ترفعه يدويًا.
          </p>
        </div>

        {results.length > 0 && (
          <div className="rounded-2xl border bg-white p-4">
            <h3 className="font-bold mb-2">نتيجة آخر رفع</h3>
            <ul className="space-y-1 text-sm">
              {results.map((result) => (
                <li key={result.id} className={result.ok ? 'text-emerald-700' : 'text-red-700'}>
                  {result.ok && !result.skipped && (
                    <>
                      ✓ رُفع الفحص برقم <b dir="ltr">{result.code}</b>
                      {result.code_changed && (
                        <span className="text-amber-700">
                          {' '}— تغيّر الرقم من <b dir="ltr">{result.requested_code}</b> لأنه كان مستخدمًا لفحص آخر.
                        </span>
                      )}
                    </>
                  )}
                  {result.ok && result.skipped && <>✓ الفحص {result.id} رُفع سابقًا.</>}
                  {!result.ok && <>✗ {result.id.replace(/^L/, '')}: {result.error}</>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {!drafts.length && (
          <div className="rounded-2xl border bg-white p-6 text-center text-slate-600">
            لا توجد فحوصات محفوظة على هذا الجهاز بانتظار الرفع.
          </div>
        )}

        <div className="grid gap-3 md:grid-cols-2">
          {drafts.map((draft) => {
            const blocker = syncBlocker(draft, user);
            const completed = draft.status === 'completed';
            return (
              <div key={draft.id} className="rounded-2xl border bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <SyncIconButton
                        draftId={draft.id}
                        lastError={blocker || draft.last_error || ''}
                        onSynced={(result) => setResults([{ id: draft.id, ...result }])}
                      />
                      <p className="text-lg font-bold" dir="ltr">#{draft.code}</p>
                    </div>
                    <p className="text-xs text-slate-500">{formatDate(draft.created_at)}</p>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${completed ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'}`}>
                    {completed ? 'مكتمل — بانتظار الرفع' : 'قيد الإدخال'}
                  </span>
                </div>

                <div className="mt-2 text-sm text-slate-700 space-y-0.5">
                  {draft.vin && <p>VIN: <span dir="ltr">{draft.vin}</span></p>}
                  {draft.plate && <p>اللوحة: {draft.plate}</p>}
                  {draft.owner_name && <p>المالك: {draft.owner_name}</p>}
                  <p className="text-slate-500">
                    بانتظار الرفع: {draft.pending_ops} عملية، منها {draft.pending_photos} صورة
                    {draft.server_id ? ' (أُنشئ في الخادم، يكمل الباقي)' : ''}
                  </p>
                </div>

                {(draft.last_error || blocker) && (
                  <p className="mt-2 rounded-lg bg-red-50 p-2 text-sm text-red-700">{blocker || draft.last_error}</p>
                )}

                <div className="mt-3 flex flex-wrap gap-2">
                  <Link to={`/inspection/${draft.id}/menu`} className="rounded-lg border px-3 py-1.5 text-sm">فتح</Link>
                  <button
                    type="button"
                    onClick={() => removeDraft(draft)}
                    disabled={progress.current === draft.id}
                    className="rounded-lg border border-red-300 px-3 py-1.5 text-sm text-red-700 disabled:opacity-50"
                  >
                    حذف من الجهاز
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
}
